/**
 * @author        tae@zumper.com (Tae Yoon)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Filter Bizos
 */

import type { Filters } from '@zcode/state-type'
import deepFreeze from 'deep-freeze'
import * as FilterC from '../../shared/constants/filter'

type PartialFilters = Omit<Partial<Filters>, 'propertyCategories' | 'sources'> & {
  propertyCategories?: Partial<Filters['propertyCategories']>
  sources?: Partial<Filters['sources']>
}

interface FilterRecordType {
  new (data: PartialFilters): Filters
  (data: PartialFilters): Filters
}

// @ts-expect-error impl doesn't look newable to TS
export const FilterRecordType: FilterRecordType = function FilterRecordType(
  data?: PartialFilters
): Filters {
  if (!data) {
    return defaultFilters
  }
  return {
    ...defaultFilters,
    ...data,
    propertyCategories: { ...defaultFilters.propertyCategories, ...data.propertyCategories },
    sources: { ...defaultFilters.sources, ...data.sources },
  }
}

export const defaultFilters: Filters = deepFreeze({
  ageRestricted: null,
  availableAfter: null,
  availableBefore: null,
  bathroomsRange: [],
  bedrooms: [false, false, false, false, false, false],
  buildingAmenities: [],
  cats: null,
  customMaxPrice: false,
  dogs: null,
  // for Flaura web extension tool
  encodedFilters: null,
  [FilterC.FILTER_TYPES.END_DATE]: null,
  external: true,
  externalRating: {
    min: null,
    max: null,
  },
  features: [],
  feeds: null,
  floorplans: null,
  guests: null,
  hasImages: null,
  hasIframe: null,
  hasVirtualTours: null,
  hideNoVacancy: null,
  isZappable: null,
  keywords: null,
  listingAmenities: [],
  liveByPolygons: [],
  longTerm: null,
  fillElevated: null,
  liveOnlineTours: null,
  minActiveUnits: null,
  maxLat: null,
  maxLng: null,
  maxDays: null,
  maxNightlyPrice: FilterC.MAX_NIGHTLY_FILTER_PRICE,
  maxPrice: FilterC.MAX_FILTER_PRICE,
  maxPricePerBedroom: null,
  maxPricePerBedroomPercentile: null,
  maxSquareFeet: null,
  minBathrooms: null,
  minLat: null,
  minLng: null,
  minNightlyPrice: FilterC.MIN_FILTER_PRICE,
  minPrice: FilterC.MIN_FILTER_PRICE,
  minPricePerBedroomPercentile: null,
  minSquareFeet: null,
  mmFrom: null,
  neighborhoodIds: [],
  noFees: null,
  orAmenities: [],
  orFeatures: [],
  pad: null,
  pets: [],
  polygons: [],
  propertyCategories: {
    apartment: null,
    condo: null,
    house: null,
    room: null,
    townhouse: null,
    other: null,
  },
  propertyTypes: [],
  Q: null,
  qs: null,
  incomeRestricted: null,
  rating: {
    min: null,
    max: null,
  },
  recommended: null,
  section8: null,
  shortTerm: null,
  sort: [],
  sources: {
    apartmentSearch: null,
    homesuite: null,
    forRent: null,
    other: null,
  },
  [FilterC.FILTER_TYPES.START_DATE]: null,
  studentHousing: null,
  transits: {},
  url: null,
  waitlisted: null,
  videoTours: null,
})

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  fromJS: (data?: PartialFilters | null | undefined): Filters => {
    if (data) {
      return new FilterRecordType({
        ageRestricted: data.ageRestricted,
        availableAfter: data.availableAfter,
        availableBefore: data.availableBefore,
        bathroomsRange: data.bathroomsRange ?? [],
        bedrooms: data.bedrooms ?? [false, false, false, false, false, false],
        buildingAmenities: data.buildingAmenities ?? [],
        cats: data.cats,
        customMaxPrice: data.customMaxPrice,
        dogs: data.dogs,
        // for Flaura web extension tool
        encodedFilters: data.encodedFilters,
        [FilterC.FILTER_TYPES.END_DATE]: data[FilterC.FILTER_TYPES.END_DATE],
        external: typeof data.external === 'undefined' ? true : data.external,
        externalRating: data.externalRating,
        features: data.features ?? [],
        feeds: data.feeds,
        floorplans: data.floorplans,
        hasIframe: data.hasIframe,
        hasImages: data.hasImages,
        hasVirtualTours: data.hasVirtualTours,
        hideNoVacancy: data.hideNoVacancy,
        isZappable: data.isZappable,
        keywords: data.keywords,
        listingAmenities: data.listingAmenities ?? [],
        liveByPolygons: data.liveByPolygons ?? [],
        longTerm: data.longTerm,
        fillElevated: data.fillElevated,
        guests: data.guests ?? null,
        liveOnlineTours: data.liveOnlineTours,
        minActiveUnits: data.minActiveUnits,
        maxLat: data.maxLat,
        maxLng: data.maxLng,
        maxDays: data.maxDays,
        maxPrice: data.maxPrice !== undefined ? data.maxPrice : FilterC.MAX_FILTER_PRICE,
        maxNightlyPrice:
          data.maxNightlyPrice !== undefined
            ? data.maxNightlyPrice
            : FilterC.MAX_NIGHTLY_FILTER_PRICE,
        maxPricePerBedroom: data.maxPricePerBedroom,
        maxPricePerBedroomPercentile: data.maxPricePerBedroomPercentile,
        maxSquareFeet: data.maxSquareFeet,
        minBathrooms: data.minBathrooms,
        minLat: data.minLat,
        minLng: data.minLng,
        minPrice: data.minPrice !== undefined ? data.minPrice : FilterC.MIN_FILTER_PRICE,
        minPricePerBedroomPercentile: data.minPricePerBedroomPercentile,
        minNightlyPrice:
          data.minNightlyPrice !== undefined ? data.minNightlyPrice : FilterC.MIN_FILTER_PRICE,
        minSquareFeet: data.minSquareFeet,
        mmFrom: data.mmFrom,
        neighborhoodIds: data.neighborhoodIds ?? [],
        noFees: data.noFees,
        orAmenities: data.orAmenities ?? [],
        orFeatures: data.orFeatures ?? [],
        pad: data.pad,
        pets: data.pets ?? [],
        polygons: data.polygons ?? [],
        propertyCategories: data.propertyCategories,
        propertyTypes: data.propertyTypes ?? [],
        Q: data.Q,
        qs: data.qs,
        incomeRestricted: data.incomeRestricted,
        rating: data.rating,
        recommended: data.recommended,
        section8: data.section8,
        shortTerm: data.shortTerm,
        sort: data.sort ?? [],
        sources: data.sources,
        [FilterC.FILTER_TYPES.START_DATE]: data[FilterC.FILTER_TYPES.START_DATE],
        studentHousing: data.studentHousing,
        transits: data.transits ?? {},
        url: data.url,
        videoTours: data.videoTours,
        waitlisted: data.waitlisted,
      })
    }
    return defaultFilters
  },
}
